import { css } from '@emotion/react';

import { useRouter } from 'next/router';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { media } from '@styles/css';
import { CurationViewType, useAnalytics } from '@utils/analytics';

import { fetchBanners } from '../../api/common';
import { IBanner, IEFinderObject } from '../../model';
import { useWebView } from '../../utils/webview';
import Border, { BorderType } from '../layout/Border';
import { EFinderImageSizes, OptimizeEFinderImage } from './EFinderImage';

export const enum BannerSize {
  curation = '38.8889%',
  promotion = '20%',
  home = '56.25%',
  playHome = '48%',
}

export interface BannerProps {
  name: string;
  size?: BannerSize;
  items: IBanner[];
  isBorder?: boolean;
  priority?: boolean;
  autoPlay?: boolean;
  allowVideo?: boolean;
  videoLoadEnabled?: boolean;
  imageHeight?: EFinderImageSizes;
}

const bannerItemCSS = (size: number | string) => css`
  position: relative;
  padding-bottom: ${typeof size === 'number' ? `${size}px` : size};
`;

const videoCSS = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const responsiveBannerCSS = css`
  ${media.tablet} {
    max-width: 500px;
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;
  }
  ${media.desktop} {
    max-width: 560px;
    border-radius: 16px;
  }
`;

type BannerVideoProps = {
  active: boolean;
  autoPlay: boolean;
  loop: boolean;
  onEnded?: () => void;
  poster?: string;
  video: IEFinderObject<'video'>;
};

const BannerVideo = ({
  active,
  autoPlay,
  loop,
  onEnded,
  poster,
  video,
}: BannerVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = video.attachment?.url;

  useEffect(() => {
    const videoElement = videoRef.current;

    if (!videoElement) {
      return;
    }

    if (!active || !autoPlay) {
      videoElement.pause();

      if (!active && videoElement.currentTime !== 0) {
        videoElement.currentTime = 0;
      }
      return;
    }

    videoElement.play().catch(() => undefined);
  }, [active, autoPlay, src]);

  if (!src) {
    return null;
  }

  return (
    <video
      ref={videoRef}
      css={videoCSS}
      src={src}
      poster={poster}
      muted
      playsInline
      loop={loop}
      onEnded={onEnded}
      preload="auto"
    />
  );
};

const getBannerHeight = (item: IBanner) =>
  (item.image?.attachment?.height ?? item.video?.attachment?.height ?? 150) / 3;

const hasVideoSource = (item: IBanner) => !!item.video?.attachment?.url;

const AUTO_PLAY_INTERVAL_MS = 3000;

const Banner = ({
  name,
  isBorder = true,
  size,
  items,
  priority = false,
  autoPlay = true,
  allowVideo = false,
  videoLoadEnabled = true,
  imageHeight = EFinderImageSizes.banner,
}: BannerProps) => {
  const { platform } = useGlobalContext();
  const { web } = useWebView();
  const { firebase } = useAnalytics();
  const router = useRouter();
  const [activeIndex, setActiveIndex] = useState(0);
  const [retainedVideoIds, setRetainedVideoIds] = useState<number[]>([]);
  const hasVideoItems =
    allowVideo &&
    items.some((item) => item.mediaType === 'video' && hasVideoSource(item));
  const activeItem = items[activeIndex];
  const isActiveVideoMounted =
    videoLoadEnabled &&
    !!activeItem &&
    allowVideo &&
    activeItem.mediaType === 'video' &&
    hasVideoSource(activeItem);
  const advanceActiveItem = useCallback(
    (expectedIndex: number) => {
      setActiveIndex((currentIndex) => {
        if (currentIndex !== expectedIndex) {
          return currentIndex;
        }

        return (currentIndex + 1) % items.length;
      });
    },
    [items.length]
  );

  useEffect(() => {
    if (activeIndex >= items.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, items.length]);

  useEffect(() => {
    if (!isActiveVideoMounted || !activeItem) {
      return;
    }

    setRetainedVideoIds((currentIds) =>
      currentIds.includes(activeItem.id)
        ? currentIds
        : [...currentIds, activeItem.id]
    );
  }, [activeItem, isActiveVideoMounted]);

  useEffect(() => {
    const currentVideoIds = new Set(
      items
        .filter((item) => item.mediaType === 'video' && hasVideoSource(item))
        .map((item) => item.id)
    );

    setRetainedVideoIds((currentIds) => {
      const validIds = currentIds.filter((id) => currentVideoIds.has(id));

      return validIds.length === currentIds.length ? currentIds : validIds;
    });
  }, [items]);

  useEffect(() => {
    const shouldAutoAdvanceWithTimer =
      autoPlay &&
      hasVideoItems &&
      items.length > 1 &&
      !!activeItem &&
      !isActiveVideoMounted;

    if (!shouldAutoAdvanceWithTimer) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => advanceActiveItem(activeIndex),
      AUTO_PLAY_INTERVAL_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    activeIndex,
    activeItem,
    advanceActiveItem,
    autoPlay,
    hasVideoItems,
    isActiveVideoMounted,
    items.length,
  ]);

  if (items.length === 0) {
    return null;
  }

  const carouselAutoPlay = autoPlay && !hasVideoItems;

  return (
    <>
      {isBorder && <Border type={BorderType.space} />}
      <Carousel
        css={responsiveBannerCSS}
        autoPlay={carouselAutoPlay}
        selectedItem={activeIndex}
        showIndicators={items.length > 1}
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        infiniteLoop={!hasVideoItems}
        emulateTouch
        onChange={setActiveIndex}
        onClickItem={(i) => {
          if (name === 'play-full-screen=banner') {
            firebase.logEvent('chatbot_click_play_curation', {
              curation_view_type: 'full_screen_banner' as CurationViewType,
            });
          }
          const { url } = items[i];
          if (url) {
            if (platform === Platform.web) {
              router.push(url);
            }
            web.openUrl(url);
          }
        }}
      >
        {items.map((item, idx) => {
          const image = item.image;
          const video =
            allowVideo && item.mediaType === 'video' ? item.video : undefined;
          const isActive = idx === activeIndex;
          const shouldMountVideo =
            videoLoadEnabled &&
            !!video?.attachment?.url &&
            (isActive || retainedVideoIds.includes(item.id));
          const shouldAutoAdvanceVideo =
            autoPlay && items.length > 1 && isActive;

          return (
            <div
              key={`banner-${name}-${item.id}`}
              css={bannerItemCSS(size ?? getBannerHeight(item))}
            >
              {shouldMountVideo && video ? (
                <BannerVideo
                  active={isActive}
                  autoPlay={autoPlay}
                  loop={!shouldAutoAdvanceVideo}
                  onEnded={
                    shouldAutoAdvanceVideo
                      ? () => advanceActiveItem(idx)
                      : undefined
                  }
                  poster={image?.attachment?.url}
                  video={video}
                />
              ) : (
                image && (
                  <OptimizeEFinderImage
                    image={image}
                    h={imageHeight}
                    loading={idx === 0 && priority ? 'eager' : 'lazy'}
                    priority={idx === 0 && priority}
                  />
                )
              )}
            </div>
          );
        })}
      </Carousel>
    </>
  );
};

export default Banner;

export const enum BannerKey {
  story = 'story',
  myStory = 'my_story',
}

type KeyBannerProps = {
  bKey: BannerKey | string;
  size?: BannerSize;
};

export const KeyBanner = ({ bKey, size }: KeyBannerProps) => {
  const query = useQuery(['banners', bKey], () => fetchBanners(bKey), {
    suspense: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const { data } = query;

  if (!data) {
    return null;
  }

  return <Banner name={bKey} size={size} items={data} />;
};
